import { useReportContext } from '@/app/features/reports/contexts/report-context';
import { cn } from '@/lib/utils';
import { RatingScale as RatingScaleType } from '@app.raytd.com/common/types/rating-scale';
import { useMemo } from 'react';
import { ReportEntity } from 'store/src/lib/services/types';
import { RatingScale } from './rating-scale';
import { useReportViewContext } from "./report-viewer-context";
import { SectionText } from './section-text';

export function RatingSystemSection() {
  const { report, filteredData, isSdaFormat } = useReportViewContext();
  const { revision } = useReportContext();

  const { ratingTypes, hiddenTestSuites } = useMemo(() => {


    if (!report?.testSuites || !filteredData) {
      return { ratingTypes: [], hiddenTestSuites: new Map() };
    }

    const activeTestSuiteIds = new Set();
    const excludedByInspection = new Map();
  
    for (const item of filteredData) {
      const { assessmentId, exclusionReason, testSpecification } = item;
      const { testSuiteId } = testSpecification;
  
      if (exclusionReason === "included") {
        activeTestSuiteIds.add(testSuiteId);
      } else {
        // This item is excluded, so group its testSuiteId by its assessmentId
        if (!excludedByInspection.has(assessmentId)) {
          excludedByInspection.set(assessmentId, new Set());
        }
        // Add the testSuiteId to the set for that inspection
        excludedByInspection.get(assessmentId).add(testSuiteId);
      }
    }
  
    const ratingScaleMap = new Map();
    // Filter active test suites to build the rating scale information
    const activeTestSuites = report.testSuites.filter(suite => activeTestSuiteIds.has(suite.id));
  
    for (const suite of activeTestSuites) {
      const { ratingScale } = suite;
      if (ratingScale) {
        if (!ratingScaleMap.has(ratingScale.id)) {
          ratingScaleMap.set(ratingScale.id, {
            ...ratingScale,
            titles: [],
          });
        }
        ratingScaleMap.get(ratingScale.id).titles.push(suite.title);
      }
    }
  
    const combinedRatingScales = Array.from(ratingScaleMap.values()).map(
      (ratingScale) => ({
        ...ratingScale,
        titles: ratingScale.titles.join(', '),
      })
    );
  
    const sortedRatingTypes = combinedRatingScales.sort((a, b) =>
      a.type.localeCompare(b.type)
    );
  
    // Convert the sets of suite IDs into arrays for the final output
    const finalHiddenTestSuites = new Map<number, number[]>();
    for (const [assessmentId, suiteIdsSet] of excludedByInspection.entries()) {
      finalHiddenTestSuites.set(assessmentId, Array.from(suiteIdsSet));
    }

    return { ratingTypes: sortedRatingTypes, hiddenTestSuites: finalHiddenTestSuites };

  }, [report, filteredData]);

  // raytd/web#218: SDA's rating scale is always the same fixed Compliance Type B scale, so a
  // per-report legend is redundant — never render this section for SDA, in any of the three
  // contexts IntroductionEditor is shared across (draft editor, interactive published view,
  // print). After every hook, per the Rules of Hooks — this can't short-circuit above them.
  if (isSdaFormat) return null;

  return (
    <section className="space-y-6">
      <div className="mb-2">
      <h2 className="font-medium mb-2 text-lg print:text-xl">Rating Systems</h2>
      <div className='text-sm'>Results shown in this report use one, or more, Raytd standardised Rating Systems.</div>
      </div>

      <SectionText
        position="introduction-rating-system"
      />

      <div className="mb-6 space-y-6">
        {ratingTypes.map((scale) => (
          <div key={scale.id} className="space-y-6 mb-6 print-rating-scale">
            <RatingScale
              key={scale.id}
              scale={scale as any}
            // customDescription={customDescriptions[scale.id] || ''}
            // onUpdateDescription={onUpdateRatingDescription}
            // editMode={editMode}
            />
            <SectionText
              position={`introduction-rating-system-${scale.id}`}
            />
          </div>
        ))}
      </div>

      {revision?.status === 'draft' && hiddenTestSuites.size > 0 && (
        <InactiveTestSuites hiddenTestSuites={hiddenTestSuites} className="mb-6" />
      )}
    </section>
  )
}

interface InactiveTestSuitesProps {
  hiddenTestSuites: Map<number, number[]>;
  className?: string;
}

function InactiveTestSuites({ hiddenTestSuites, className }: InactiveTestSuitesProps) {
  const { report } = useReportViewContext();

  const suitesById = useMemo(() => 
    new Map(report.testSuites.map(suite => [suite.id, suite])), 
    [report.testSuites]
  );

  if (hiddenTestSuites.size === 0) {
    return null;
  }

  return (
    <div className={cn("p-4 border border-dashed border-muted-foreground/50 rounded-lg bg-muted/20", className)}>
      <h4 className="font-medium mb-2 text-base text-muted-foreground">Inactive Test Suites</h4>
      <p className="text-sm text-muted-foreground mb-2">The following Test Suites have no Included Tests:</p>
      <div className="space-y-4">
        {/* Convert the Map to an array and loop over each [assessmentId, suiteIds] pair */}
        {Array.from(hiddenTestSuites.entries()).map(([assessmentId, suiteIds]) => (
          <div key={assessmentId}>
            <p className="text-sm font-semibold text-muted-foreground mb-1">
              {report.assessments.find(assessment => assessment.id === assessmentId)?.title}
            </p>
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-3">
              {suiteIds.map(suiteId => {
                // Find the full suite object from our lookup map
                const suite = suitesById.get(suiteId);
                // Render nothing if a suite isn't found, just in case
                if (!suite) return null;
                return <li key={suite.id}>{suite.title}</li>;
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}